import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })
      .format(date).replace(":", ".").replace(/\s+/g, " ").trim();
    return `${datePart} ${timePart}`;
  } catch { return String(value); }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "-";
  const fmt = (value) => {
    if (!value) return "";
    try { return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)); }
    catch { return String(value); }
  };
  if (startDate && endDate) return `${fmt(startDate)} to ${fmt(endDate)}`;
  return fmt(startDate) || fmt(endDate) || "-";
};

const toText = (value, fallback = "-") => { const t = String(value ?? "").trim(); return t || fallback; };

const formatCustomerUserLabel = (item) => {
  const name = toText(item?.customerUserName, "Unknown User");
  const email = String(item?.customerUserEmail ?? "").trim();
  return email ? `${name} (${email})` : name;
};

const formatEmployeeLabel = (item) => {
  const name = toText(item?.employeeName, "Unassigned");
  const email = String(item?.employeeEmail ?? "").trim();
  return email ? `${name} (${email})` : name;
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  try { return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(new Date(value)); }
  catch { return String(value); }
};

const getStatusTone = (status) => {
  const n = String(status ?? "").trim().toLowerCase();
  if (n === "resolved") return { bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" };
  if (n === "in progress") return { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" };
  if (n === "on hold") return { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" };
  if (n === "new") return { bg: "#ede9fe", text: "#5b21b6", border: "#c4b5fd" };
  return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
};

export function TicketSummaryPdfDocument({
  components, logoUrl, companyInfo, filters, report, selectedColumns, generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;

  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["ticketName", "organization", "employee", "customer", "customerUser", "openDate", "status", "taskCount"];

  const colDefs = [
    { key: "ticketName", header: "Ticket", flex: 2.2, strong: true },
    { key: "organization", header: "Organization", flex: 1.4 },
    { key: "employee", header: "Assigned To", flex: 1.6 },
    { key: "customer", header: "Customer", flex: 1.4, strong: true },
    { key: "customerUser", header: "Customer User", flex: 1.6 },
    { key: "openDate", header: "Date", flex: 1.0 },
    { key: "status", header: "Status", flex: 1.0, isStatus: true },
    { key: "taskCount", header: "Tasks", flex: 0.6, center: true },
  ].filter((c) => activeColumns.includes(c.key));

  const pageOrientation = activeColumns.length <= 4 ? "portrait" : "landscape";
  const serialColumnWidth = 22;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 32,
      paddingBottom: 30,
      paddingHorizontal: 32,
      backgroundColor: "#ffffff",
      fontSize: 9,
      color: "#0f172a",
      fontFamily: "HindSiliguri",
    },

    /* ── Header ── */
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      marginBottom: 12,
    },
    brandRow: { flexDirection: "column", alignItems: "flex-start" },
    logo: { width: 100, objectFit: "contain", marginBottom: 2 },
    companyName: { fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: -0.2 },
    companyTagline: { fontSize: 8, color: "#64748b", marginTop: 1, letterSpacing: 0.3 },
    contactBlock: { alignItems: "flex-start" },
    contactRow: { flexDirection: "row", alignItems: "center", marginBottom: 3 },
    contactLabel: { fontSize: 8, fontWeight: 700, color: "#0f172a", width: 52 },
    contactLine: { fontSize: 8, color: "#475569" },

    /* ── Report meta bar ── */
    metaBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 6,
      paddingVertical: 7,
      paddingHorizontal: 11,
      marginBottom: 14,
    },
    metaLeft: { flexDirection: "column" },
    metaReportName: { fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 1 },
    metaFilter: { fontSize: 7.8, color: "#475569", lineHeight: 1.55 },
    metaRight: { alignItems: "flex-end" },
    metaGenLabel: { fontSize: 7, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 1 },
    metaGenValue: { fontSize: 8, color: "#334155" },

    /* ── Table ── */
    table: {
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 7,
      overflow: "hidden",
    },
    tHead: {
      flexDirection: "row",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tHeadCell: {
      fontSize: 7.4,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 7,
    },
    tRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#f1f5f9",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tRowAlt: { backgroundColor: "#f8fafc" },
    tRowLast: { borderBottomWidth: 0 },
    cell: { fontSize: 8.2, color: "#1e293b", lineHeight: 1.45, paddingHorizontal: 7 },
    cellBold: { fontSize: 8.2, fontWeight: 700, color: "#0f172a", lineHeight: 1.45, paddingHorizontal: 7 },
    cellCenter: { fontSize: 8.2, color: "#334155", lineHeight: 1.45, paddingHorizontal: 7, textAlign: "center" },
    statusWrap: { justifyContent: "center", alignItems: "flex-start", paddingHorizontal: 7 },
    statusPill: { borderWidth: 1, borderRadius: 999, paddingHorizontal: 6, paddingVertical: 2.5, alignSelf: "flex-start" },
    statusText: { fontSize: 7.4, fontWeight: 700, lineHeight: 1 },
    serialHeadCell: {
      fontSize: 7.4,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 7,
      textAlign: "center",
    },
    serialCell: {
      paddingHorizontal: 7,
      fontSize: 8.2,
      fontWeight: 700,
      color: "#0f172a",
      textAlign: "center",
    },

    /* ── Empty / Footer ── */
    emptyBox: {
      borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 7,
      paddingVertical: 20, alignItems: "center",
    },
    emptyText: { fontSize: 9, color: "#94a3b8" },
    footer: {
      marginTop: 10, paddingTop: 8,
      borderTopWidth: 1, borderTopColor: "#e2e8f0",
      flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    },
    footerText: { fontSize: 7.6, color: "#94a3b8" },
  });

  const filterParts = [
    `Period: ${formatDateRange(filters?.startDate, filters?.endDate)}`,
    filters?.organizationName && filters.organizationName !== "All Organizations" ? `Org: ${filters.organizationName}` : null,
    filters?.employeeName && filters.employeeName !== "All Employees" ? `Employee: ${filters.employeeName}` : null,
    filters?.customerName && filters.customerName !== "All Customers" ? `Customer: ${filters.customerName}` : null,
    filters?.customerUserName && filters.customerUserName !== "All Customer Users" ? `User: ${filters.customerUserName}` : null,
    filters?.status && filters.status !== "All Statuses" ? `Status: ${filters.status}` : null,
    filters?.customerCode && filters.customerCode !== "All" ? `Code: ${filters.customerCode}` : null,
  ].filter(Boolean);

  const totalItems = report?.items?.length || 0;

  return (
    <Document
      title="Ticket Summary Report"
      author="BanglaFire CRM"
      subject="Ticket summary"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" orientation={pageOrientation} style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>BanglaFire CRM</Text>
            <Text style={styles.companyTagline}>Customer Relationship Management</Text>
          </View>
          <View style={styles.contactBlock}>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Address :</Text>
              <Text style={styles.contactLine}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Mobile :</Text>
              <Text style={styles.contactLine}>(+880) 9639222999  |  1709927934</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Email :</Text>
              <Text style={styles.contactLine}>md@banglafire.com  |  support@banglafire.com</Text>
            </View>
          </View>
        </View>

        {/* ── META BAR ── */}
        <View style={styles.metaBar}>
          <View style={styles.metaLeft}>
            <Text style={styles.metaReportName}>Ticket Summary Report</Text>
            {filterParts.length > 0 && (
              <Text style={styles.metaFilter}>{filterParts.join("   •   ")}</Text>
            )}
          </View>
          <View style={styles.metaRight}>
            <Text style={styles.metaGenLabel}>Generated</Text>
            <Text style={styles.metaGenValue}>{formatDateTime(generatedAt)}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Total Records</Text>
            <Text style={[styles.metaGenValue, { fontWeight: 700, color: "#0f172a" }]}>{totalItems}</Text>
          </View>
        </View>

        {/* ── TABLE ── */}
        {totalItems > 0 ? (
          <View style={styles.table}>
            <View style={styles.tHead}>
              <Text style={[styles.serialHeadCell, { width: serialColumnWidth }]}>#</Text>
              {colDefs.map((col) => (
                <Text
                  key={col.key}
                  style={[styles.tHeadCell, { flex: col.flex, textAlign: col.center ? "center" : "left" }]}
                >
                  {col.header}
                </Text>
              ))}
            </View>
            {report.items.map((item, idx) => {
              const tone = getStatusTone(item.status);
              const isLast = idx === report.items.length - 1;
              return (
                <View
                  key={`row-${idx}`}
                  style={[styles.tRow, idx % 2 === 1 ? styles.tRowAlt : null, isLast ? styles.tRowLast : null]}
                  wrap={false}
                >
                  <Text style={[styles.serialCell, { width: serialColumnWidth }]}>{idx + 1}</Text>
                  {colDefs.map((col) => {
                    const w = { flex: col.flex };
                    if (col.isStatus) {
                      return (
                        <View key={col.key} style={[styles.statusWrap, w]}>
                          <View style={[styles.statusPill, { backgroundColor: tone.bg, borderColor: tone.border }]}>
                            <Text style={[styles.statusText, { color: tone.text }]}>{toText(item.status)}</Text>
                          </View>
                        </View>
                      );
                    }
                    const textStyle = col.strong ? styles.cellBold : col.center ? styles.cellCenter : styles.cell;
                    let value = "-";
                    if (col.key === "ticketName") value = toText(item.ticketName);
                    if (col.key === "organization") value = toText(item.organizationName);
                    if (col.key === "employee") value = formatEmployeeLabel(item);
                    if (col.key === "customer") value = toText(item.customerName);
                    if (col.key === "customerUser") value = formatCustomerUserLabel(item);
                    if (col.key === "openDate") value = formatDateOnly(item.openDate);
                    if (col.key === "taskCount") value = String(item.taskCount || 0);
                    return <Text key={col.key} style={[textStyle, w]}>{value}</Text>;
                  })}
                </View>
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No tickets matched the selected filters.</Text>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM  |  Confidential</Text>
        </View>
      </Page>
    </Document>
  );
}
